// ═══════════════════════════════════════════════════════════════════════════════
// EmergenceLayer223  — ENHANCED VERSION
// 223 Enhancement & Emergence Layer for 360-IF Engine
//
// Features:
//   • Pre-computing H-dim embeddings with batching and caching
//   • Semantic clustering of document chunks
//   • Topic extraction and modeling
//   • Key phrase identification
//   • Response coherence scoring (α-coupling model)
//   • Confidence calibration
//   • Evidence enrichment with ranking
//   • Quality flagging and metrics
//   • Passage-response alignment scoring
//   • Sentence-level similarity analysis
//   • Writing quality indicators
//
// All methods include error handling and graceful fallbacks.
// ═══════════════════════════════════════════════════════════════════════════════

import { C } from './constants.js';
import { encode, forward, isModelTrained } from './neuralNetwork.js';

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const CACHE_MAX_SIZE = 500;
const CLUSTER_SIMILARITY_THRESHOLD = 0.7;
const MIN_CHUNK_LENGTH = 20;
const MAX_CACHE_AGE_MS = 5 * 60 * 1000; // 5 minutes

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN MODULE
// ═══════════════════════════════════════════════════════════════════════════════

const EmergenceLayer223 = {
  // ── State ────────────────────────────────────────────────────────────────
  enabled: true,
  status: 'pending',
  loadProgress: 0,
  alphaThreshold: C.ALPHA,

  // ── Embeddings Storage ───────────────────────────────────────────────────
  chunkEmbeddings: null,
  chunkTexts: null,
  sentenceEmbeddings: null,

  // ── Semantic Analysis ────────────────────────────────────────────────────
  semanticClusters: null,
  topicVectors: null,
  keyPhrases: [],
  documentCentroid: null,

  // ── Caching ──────────────────────────────────────────────────────────────
  queryCache: new Map(),
  embeddingCache: new Map(),
  cacheTimestamps: new Map(),

  // ── Statistics ───────────────────────────────────────────────────────────
  stats: {
    totalCalls: 0,
    avgLatency: 0,
    successRate: 1.0,
    cacheHits: 0,
    cacheMisses: 0,
    nativeQueries: 0,
    pure223Queries: 0,
    regexFallbacks: 0,
    specializedHandlers: 0,
    level1to50Handlers: 0,
    emergence223: 0,
    neuralPatternMatches: 0,
    neuralPatternBypassed: 0,
    enhanced223Passes: 0,
    coherenceBoosts: 0,
    evidenceAdditions: 0,
    confidenceAdjustments: 0,
    qualityFlags: 0,
    structuralEnhancements: 0,
    clusteringRuns: 0,
    topicExtractions: 0
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════════════

  async initialize(progressCallback) {
    try {
      this.status = 'initializing';
      this.initWordVectors();
      this.initTopicVectors();
      this.status = 'ready';
      if (progressCallback) {
        progressCallback({ status: 'ready', progress: 1.0, mode: 'native' });
      }
      console.log('[223 EMERGENCE] Initialized successfully');
      return true;
    } catch (err) {
      console.error('[223 EMERGENCE] Initialization error:', err);
      this.status = 'error';
      return false;
    }
  },

  reset() {
    this.chunkEmbeddings = null;
    this.chunkTexts = null;
    this.sentenceEmbeddings = null;
    this.semanticClusters = null;
    this.keyPhrases = [];
    this.documentCentroid = null;
    this.queryCache.clear();
    this.embeddingCache.clear();
    this.cacheTimestamps.clear();
    this.status = 'pending';
    console.log('[223 EMERGENCE] Reset complete');
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // EMBEDDING COMPUTATION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Pre-compute H-dim embeddings for document chunks
   * @param {Array} chunks - Array of chunk objects or strings
   * @param {Function} progressCallback - Progress reporting function
   * @returns {boolean} Success status
   */
  async precomputeEmbeddings(chunks, progressCallback) {
    if (!isModelTrained()) {
      console.warn('[223 EMERGENCE] Model not trained, skipping embeddings');
      return false;
    }

    if (!chunks || chunks.length === 0) {
      console.warn('[223 EMERGENCE] No chunks provided');
      return false;
    }

    const start = performance.now();

    try {
      this.chunkEmbeddings = new Array(chunks.length);
      this.chunkTexts = new Array(chunks.length);

      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        const text = typeof chunk === 'string' ? chunk : (chunk.text || chunk.content || '');
        
        if (text.length < MIN_CHUNK_LENGTH) {
          this.chunkEmbeddings[i] = null;
          this.chunkTexts[i] = text;
          continue;
        }

        try {
          const encoded = encode(text);
          const { h2 } = forward(encoded);
          this.chunkEmbeddings[i] = new Float32Array(h2);
          this.chunkTexts[i] = text;
        } catch (e) {
          this.chunkEmbeddings[i] = null;
          this.chunkTexts[i] = text;
        }

        // Progress callback
        if (progressCallback && i % 5 === 0) {
          progressCallback({
            phase: 'embedding',
            processed: i + 1,
            total: chunks.length,
            percent: Math.round(((i + 1) / chunks.length) * 100)
          });
        }

        // Yield to event loop periodically
        if (i % 20 === 0 && i > 0) {
          await new Promise(r => setTimeout(r, 0));
        }
      }

      // Compute document centroid
      this._computeDocumentCentroid();

      // Run semantic clustering
      await this._clusterChunks();

      // Extract key phrases
      this._extractKeyPhrases(chunks);

      const elapsed = performance.now() - start;
      console.log(`[223 EMERGENCE] Embeddings computed in ${elapsed.toFixed(0)}ms for ${chunks.length} chunks`);

      return true;
    } catch (err) {
      console.error('[223 EMERGENCE] Embedding computation error:', err);
      return false;
    }
  },

  /**
   * Pre-compute sentence-level embeddings for finer granularity
   * @param {string} fullText - The complete document text
   */
  async precomputeSentenceEmbeddings(fullText) {
    if (!isModelTrained() || !fullText) return false;

    try {
      const sentences = this._splitIntoSentences(fullText);
      this.sentenceEmbeddings = [];

      for (const sent of sentences.slice(0, 100)) { // Limit to 100 sentences
        if (sent.length < 10) continue;
        try {
          const { h2 } = forward(encode(sent));
          this.sentenceEmbeddings.push({
            text: sent,
            embedding: new Float32Array(h2)
          });
        } catch (e) {
          // Skip failed sentences
        }
      }

      console.log(`[223 EMERGENCE] Computed ${this.sentenceEmbeddings.length} sentence embeddings`);
      return true;
    } catch (err) {
      console.error('[223 EMERGENCE] Sentence embedding error:', err);
      return false;
    }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // DOCUMENT CENTROID
  // ═══════════════════════════════════════════════════════════════════════════

  _computeDocumentCentroid() {
    if (!this.chunkEmbeddings || this.chunkEmbeddings.length === 0) return;

    const validEmbeddings = this.chunkEmbeddings.filter(e => e !== null);
    if (validEmbeddings.length === 0) return;

    const dim = validEmbeddings[0].length;
    this.documentCentroid = new Float32Array(dim);

    for (const emb of validEmbeddings) {
      for (let i = 0; i < dim; i++) {
        this.documentCentroid[i] += emb[i] / validEmbeddings.length;
      }
    }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SEMANTIC CLUSTERING
  // ═══════════════════════════════════════════════════════════════════════════

  async _clusterChunks() {
    if (!this.chunkEmbeddings || this.chunkEmbeddings.length < 2) return;

    this.stats.clusteringRuns++;
    const clusters = [];
    const assigned = new Set();

    try {
      for (let i = 0; i < this.chunkEmbeddings.length; i++) {
        if (assigned.has(i) || !this.chunkEmbeddings[i]) continue;

        const cluster = {
          centroid: null,
          members: [i],
          topicLabel: '',
          coherence: 0
        };

        // Find similar chunks
        for (let j = i + 1; j < this.chunkEmbeddings.length; j++) {
          if (assigned.has(j) || !this.chunkEmbeddings[j]) continue;

          const sim = this.cosineSimilarity223(
            this.chunkEmbeddings[i],
            this.chunkEmbeddings[j]
          );

          if (sim >= CLUSTER_SIMILARITY_THRESHOLD) {
            cluster.members.push(j);
            assigned.add(j);
          }
        }

        assigned.add(i);

        // Compute cluster centroid
        cluster.centroid = this._computeClusterCentroid(cluster.members);

        // Generate topic label from most common words
        cluster.topicLabel = this._generateClusterLabel(cluster.members);

        // Compute cluster coherence
        cluster.coherence = this._computeClusterCoherence(cluster.members);

        clusters.push(cluster);
      }

      this.semanticClusters = clusters;
      console.log(`[223 EMERGENCE] Created ${clusters.length} semantic clusters`);
    } catch (err) {
      console.warn('[223 EMERGENCE] Clustering error:', err.message);
    }
  },

  _computeClusterCentroid(memberIndices) {
    const validEmbeddings = memberIndices
      .map(i => this.chunkEmbeddings[i])
      .filter(e => e !== null);

    if (validEmbeddings.length === 0) return null;

    const dim = validEmbeddings[0].length;
    const centroid = new Float32Array(dim);

    for (const emb of validEmbeddings) {
      for (let i = 0; i < dim; i++) {
        centroid[i] += emb[i] / validEmbeddings.length;
      }
    }

    return centroid;
  },

  _generateClusterLabel(memberIndices) {
    if (!this.chunkTexts) return 'Unknown';

    const wordCounts = {};
    const stopWords = new Set(['the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'must', 'shall', 'can', 'to', 'of', 'in', 'for', 'on', 'with', 'at', 'by', 'from', 'as', 'into', 'through', 'and', 'but', 'or', 'not', 'this', 'that', 'these', 'those', 'it', 'its', 'they', 'them', 'their', 'he', 'she', 'his', 'her', 'we', 'you', 'i', 'me', 'my']);

    for (const idx of memberIndices) {
      const text = this.chunkTexts[idx] || '';
      const words = text.toLowerCase().match(/\b[a-z]{4,}\b/g) || [];
      
      for (const word of words) {
        if (!stopWords.has(word)) {
          wordCounts[word] = (wordCounts[word] || 0) + 1;
        }
      }
    }

    const topWords = Object.entries(wordCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([word]) => word);

    return topWords.length > 0 ? topWords.join(', ') : 'General';
  },

  _computeClusterCoherence(memberIndices) {
    if (memberIndices.length < 2) return 1.0;

    let totalSim = 0;
    let count = 0;

    for (let i = 0; i < memberIndices.length; i++) {
      for (let j = i + 1; j < memberIndices.length; j++) {
        const embA = this.chunkEmbeddings[memberIndices[i]];
        const embB = this.chunkEmbeddings[memberIndices[j]];
        if (embA && embB) {
          totalSim += this.cosineSimilarity223(embA, embB);
          count++;
        }
      }
    }

    return count > 0 ? totalSim / count : 0;
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // KEY PHRASE EXTRACTION
  // ═══════════════════════════════════════════════════════════════════════════

  _extractKeyPhrases(chunks) {
    this.keyPhrases = [];
    const phraseCounts = {};
    const phrasePositions = {};

    try {
      for (let i = 0; i < chunks.length; i++) {
        const text = typeof chunks[i] === 'string' ? chunks[i] : (chunks[i].text || '');
        
        // Extract noun phrases (simple heuristic)
        const nounPhrases = this._extractNounPhrases(text);
        
        for (const phrase of nounPhrases) {
          const normalized = phrase.toLowerCase();
          phraseCounts[normalized] = (phraseCounts[normalized] || 0) + 1;
          if (!phrasePositions[normalized]) {
            phrasePositions[normalized] = i;
          }
        }
      }

      // Score phrases by frequency and position (earlier = more important)
      const scoredPhrases = Object.entries(phraseCounts)
        .map(([phrase, count]) => ({
          phrase,
          count,
          position: phrasePositions[phrase],
          score: count * (1 / (1 + phrasePositions[phrase] * 0.1))
        }))
        .filter(p => p.count >= 2 || p.phrase.split(' ').length >= 2)
        .sort((a, b) => b.score - a.score)
        .slice(0, 20);

      this.keyPhrases = scoredPhrases;
      console.log(`[223 EMERGENCE] Extracted ${this.keyPhrases.length} key phrases`);
    } catch (err) {
      console.warn('[223 EMERGENCE] Key phrase extraction error:', err.message);
    }
  },

  _extractNounPhrases(text) {
    const phrases = [];
    
    // Capitalized phrases (potential proper nouns or important terms)
    const capitalizedMatches = text.match(/[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*/g) || [];
    phrases.push(...capitalizedMatches.filter(p => p.length > 3));

    // Quoted phrases
    const quotedMatches = text.match(/[""][^""]+[""]|'[^']+'/) || [];
    phrases.push(...quotedMatches.map(q => q.replace(/[""'']/g, '').trim()));

    // Common noun phrase patterns (adjective + noun)
    const adjectiveNounPattern = /\b(important|main|key|primary|significant|major|central|critical|essential)\s+(\w+)/gi;
    let match;
    while ((match = adjectiveNounPattern.exec(text)) !== null) {
      phrases.push(match[0]);
    }

    return phrases;
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TOPIC VECTORS (for untrained fallback and topic classification)
  // ═══════════════════════════════════════════════════════════════════════════

  initTopicVectors() {
    this.topicVectors = {
      // Document types
      narrative: [0.8, 0.2, 0.3, 0.1, 0.5],
      expository: [0.3, 0.7, 0.5, 0.6, 0.4],
      persuasive: [0.5, 0.5, 0.7, 0.4, 0.6],
      descriptive: [0.7, 0.4, 0.2, 0.3, 0.5],
      
      // Content areas
      science: [0.2, 0.8, 0.6, 0.7, 0.3],
      history: [0.6, 0.5, 0.4, 0.5, 0.7],
      literature: [0.8, 0.3, 0.4, 0.2, 0.6],
      social_studies: [0.5, 0.6, 0.5, 0.5, 0.5],
      
      // Writing elements
      claim: [0.4, 0.6, 0.8, 0.5, 0.4],
      evidence: [0.3, 0.8, 0.5, 0.7, 0.4],
      reasoning: [0.4, 0.7, 0.6, 0.6, 0.5],
      conclusion: [0.5, 0.5, 0.6, 0.4, 0.7]
    };
  },

  classifyTopic(embedding) {
    if (!this.topicVectors || !embedding) return { topic: 'unknown', confidence: 0 };

    let bestTopic = 'unknown';
    let bestScore = -1;

    // Use first 5 dimensions for topic matching (or pad/truncate)
    const vec = Array.isArray(embedding) ? embedding.slice(0, 5) : Array.from(embedding).slice(0, 5);
    while (vec.length < 5) vec.push(0);

    for (const [topic, topicVec] of Object.entries(this.topicVectors)) {
      const sim = this._cosineSim5D(vec, topicVec);
      if (sim > bestScore) {
        bestScore = sim;
        bestTopic = topic;
      }
    }

    return { topic: bestTopic, confidence: Math.max(0, bestScore) };
  },

  _cosineSim5D(a, b) {
    let dot = 0, magA = 0, magB = 0;
    for (let i = 0; i < 5; i++) {
      dot += (a[i] || 0) * (b[i] || 0);
      magA += (a[i] || 0) ** 2;
      magB += (b[i] || 0) ** 2;
    }
    return magA > 0 && magB > 0 ? dot / (Math.sqrt(magA) * Math.sqrt(magB)) : 0;
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // LEGACY WORD VECTORS (fallback when untrained)
  // ═══════════════════════════════════════════════════════════════════════════

  initWordVectors() {
    if (this.semanticClusters && Object.keys(this.semanticClusters).length > 10) return;
    
    this.semanticClusters = {
      // Document structure
      document: [0.8, 0.1, 0.2], paper: [0.75, 0.15, 0.2], text: [0.7, 0.2, 0.1],
      content: [0.65, 0.25, 0.1], section: [0.6, 0.3, 0.1], paragraph: [0.55, 0.35, 0.1],
      chapter: [0.58, 0.32, 0.1], page: [0.52, 0.38, 0.1], article: [0.72, 0.18, 0.1],
      
      // Analysis
      analysis: [0.2, 0.8, 0.1], summary: [0.25, 0.75, 0.1], overview: [0.3, 0.7, 0.1],
      conclusion: [0.15, 0.85, 0.1], finding: [0.2, 0.78, 0.12], result: [0.22, 0.76, 0.12],
      theme: [0.28, 0.72, 0.1], idea: [0.32, 0.68, 0.1], concept: [0.26, 0.74, 0.1],
      
      // Question words
      what: [0.1, 0.1, 0.8], how: [0.15, 0.15, 0.7], why: [0.12, 0.18, 0.7],
      when: [0.2, 0.1, 0.7], where: [0.22, 0.08, 0.7], who: [0.25, 0.05, 0.7],
      which: [0.18, 0.12, 0.7], whose: [0.23, 0.07, 0.7],
      
      // Reasoning
      cause: [0.3, 0.5, 0.2], effect: [0.32, 0.48, 0.2], reason: [0.28, 0.52, 0.2],
      because: [0.26, 0.54, 0.2], therefore: [0.24, 0.56, 0.2], thus: [0.25, 0.55, 0.2],
      
      // Argumentation
      argument: [0.35, 0.55, 0.1], claim: [0.38, 0.52, 0.1], evidence: [0.4, 0.5, 0.1],
      support: [0.42, 0.48, 0.1], prove: [0.36, 0.54, 0.1], demonstrate: [0.34, 0.56, 0.1],
      
      // Comparison
      compare: [0.45, 0.45, 0.1], contrast: [0.47, 0.43, 0.1], similar: [0.43, 0.47, 0.1],
      different: [0.48, 0.42, 0.1], same: [0.41, 0.49, 0.1],
      
      // Writing quality
      coherent: [0.5, 0.4, 0.1], organized: [0.52, 0.38, 0.1], clear: [0.48, 0.42, 0.1],
      focused: [0.54, 0.36, 0.1], developed: [0.46, 0.44, 0.1]
    };
  },

  getWordVector(word) {
    const w = word.toLowerCase();
    if (this.semanticClusters?.[w]) return this.semanticClusters[w];
    
    // Generate pseudo-random but consistent vector for unknown words
    const hash = this._hashString(w);
    return [
      (hash % 100) / 100,
      ((hash * 7) % 100) / 100,
      ((hash * 13) % 100) / 100
    ];
  },

  _hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash = hash & hash;
    }
    return Math.abs(hash);
  },

  getSentenceVector(text) {
    const words = text.toLowerCase().split(/\s+/).filter(w => w.length > 2);
    if (words.length === 0) return [0.33, 0.33, 0.33];
    
    const vecs = words.map(w => this.getWordVector(w));
    const avg = [0, 0, 0];
    vecs.forEach(v => {
      avg[0] += v[0] / vecs.length;
      avg[1] += v[1] / vecs.length;
      avg[2] += v[2] / vecs.length;
    });
    return avg;
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // QUERY EMBEDDING
  // ═══════════════════════════════════════════════════════════════════════════

  getQueryEmbedding(query, useCache = true) {
    if (!query) return null;

    // Check cache
    if (useCache && this.embeddingCache.has(query)) {
      const timestamp = this.cacheTimestamps.get(query);
      if (timestamp && Date.now() - timestamp < MAX_CACHE_AGE_MS) {
        this.stats.cacheHits++;
        return this.embeddingCache.get(query);
      }
    }

    this.stats.cacheMisses++;

    // Use neural embedding if trained
    if (isModelTrained()) {
      try {
        const encoded = encode(query);
        const { h2 } = forward(encoded);
        const embedding = Array.from(h2);

        // Cache result
        this._cacheEmbedding(query, embedding);

        return embedding;
      } catch (e) {
        console.warn('[223 EMERGENCE] Query embedding failed, using fallback');
      }
    }

    // Fallback to word vectors
    return this.getSentenceVector(query);
  },

  _cacheEmbedding(key, embedding) {
    // Evict old entries if cache is full
    if (this.embeddingCache.size >= CACHE_MAX_SIZE) {
      const oldestKey = this.embeddingCache.keys().next().value;
      this.embeddingCache.delete(oldestKey);
      this.cacheTimestamps.delete(oldestKey);
    }

    this.embeddingCache.set(key, embedding);
    this.cacheTimestamps.set(key, Date.now());
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SIMILARITY FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════

  cosineSimilarity223(a, b) {
    if (!a || !b || a.length !== b.length) return 0;
    
    let dot = 0, magA = 0, magB = 0;
    for (let i = 0; i < a.length; i++) {
      dot += a[i] * b[i];
      magA += a[i] * a[i];
      magB += b[i] * b[i];
    }
    
    return magA > 0 && magB > 0 ? dot / (Math.sqrt(magA) * Math.sqrt(magB)) : 0;
  },

  cosineSimilarity(a, b) {
    const dot = a.reduce((s, ai, i) => s + ai * (b[i] || 0), 0);
    const magA = Math.sqrt(a.reduce((s, ai) => s + ai * ai, 0));
    const magB = Math.sqrt(b.reduce((s, bi) => s + bi * bi, 0));
    return magA > 0 && magB > 0 ? dot / (magA * magB) : 0;
  },

  euclideanDistance(a, b) {
    if (!a || !b || a.length !== b.length) return Infinity;
    let sum = 0;
    for (let i = 0; i < a.length; i++) {
      sum += (a[i] - b[i]) ** 2;
    }
    return Math.sqrt(sum);
  },

  getNeuralSimilarity(queryEmbedding, chunkIndex) {
    if (!this.chunkEmbeddings || !this.chunkEmbeddings[chunkIndex]) return 0;
    return this.cosineSimilarity223(queryEmbedding, this.chunkEmbeddings[chunkIndex]);
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CHUNK RETRIEVAL
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Find the most relevant chunks for a query
   * @param {Array} queryVector - Query embedding
   * @param {Array} chunks - Original chunk objects
   * @param {number} topK - Number of chunks to return
   * @returns {Array} Ranked relevant chunks
   */
  findRelevantChunks(queryVector, chunks, topK = 3) {
    if (!this.chunkEmbeddings || this.chunkEmbeddings.length === 0) {
      return [];
    }

    const scored = [];

    for (let i = 0; i < this.chunkEmbeddings.length; i++) {
      const embedding = this.chunkEmbeddings[i];
      if (!embedding) continue;

      const score = this.cosineSimilarity223(queryVector, embedding);
      
      scored.push({
        index: i,
        text: this.chunkTexts?.[i] || (chunks?.[i]?.text) || '',
        score,
        chunk: chunks?.[i] || null
      });
    }

    return scored
      .sort((a, b) => b.score - a.score)
      .slice(0, topK);
  },

  /**
   * Find relevant chunks with semantic cluster awareness
   */
  findRelevantChunksWithClusters(queryVector, chunks, topK = 3) {
    const baseResults = this.findRelevantChunks(queryVector, chunks, topK * 2);
    
    if (!this.semanticClusters || this.semanticClusters.length === 0) {
      return baseResults.slice(0, topK);
    }

    // Boost diversity by including chunks from different clusters
    const selected = [];
    const usedClusters = new Set();

    for (const result of baseResults) {
      const cluster = this._findChunkCluster(result.index);
      
      if (!usedClusters.has(cluster) || selected.length < topK / 2) {
        selected.push(result);
        if (cluster !== null) usedClusters.add(cluster);
      }

      if (selected.length >= topK) break;
    }

    return selected;
  },

  _findChunkCluster(chunkIndex) {
    if (!this.semanticClusters) return null;
    
    for (let i = 0; i < this.semanticClusters.length; i++) {
      if (this.semanticClusters[i].members.includes(chunkIndex)) {
        return i;
      }
    }
    return null;
  },

  /**
   * Find the best sentence from a chunk that matches the query
   */
  extractBestSentence(chunkText, query) {
    const sentences = this._splitIntoSentences(chunkText);
    if (sentences.length === 0) return null;

    // Use neural similarity if available
    if (isModelTrained()) {
      try {
        const queryVec = this.getQueryEmbedding(query);
        let bestSent = sentences[0];
        let bestScore = -1;

        for (const sent of sentences) {
          if (sent.length < 20) continue;
          const sentVec = this.getQueryEmbedding(sent);
          const score = this.cosineSimilarity223(queryVec, sentVec);
          if (score > bestScore) {
            bestScore = score;
            bestSent = sent;
          }
        }

        return bestSent.trim().substring(0, 250);
      } catch (e) {
        // Fall through to keyword matching
      }
    }

    // Fallback: keyword matching
    const qWords = query.toLowerCase().split(/\s+/).filter(w => w.length > 3);
    let best = sentences[0];
    let bestScore = 0;

    for (const sent of sentences) {
      const lower = sent.toLowerCase();
      let score = 0;
      for (const w of qWords) {
        if (lower.includes(w)) score++;
      }
      if (score > bestScore) {
        bestScore = score;
        best = sent;
      }
    }

    return best.trim().substring(0, 250);
  },

  _splitIntoSentences(text) {
    return text
      .split(/[.!?]+/)
      .map(s => s.trim())
      .filter(s => s.length > 10);
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // RESPONSE ENHANCEMENT (Core 223 Pass)
  // ═══════════════════════════════════════════════════════════════════════════

  enhance(response, query, context = {}) {
    if (!response || !response.text) return response;

    const start = performance.now();
    this.stats.enhanced223Passes++;

    try {
      // ── Structural Context ──────────────────────────────────────────────
      const structure = context.documentIntelligence?.structure;
      if (structure && structure.totalParagraphs > 0) {
        this.stats.structuralEnhancements++;
        response.structuralContext = this._buildStructuralContext(structure);
      }

      // ── Coherence Scoring ───────────────────────────────────────────────
      let coherenceScore = 0;
      
      if (isModelTrained()) {
        try {
          const qVec = this.getQueryEmbedding(query);
          const rVec = this.getQueryEmbedding(response.text.substring(0, 500));
          coherenceScore = this.cosineSimilarity223(qVec, rVec);
        } catch (e) {
          coherenceScore = 0.5;
        }
      } else {
        // Fallback coherence using word vectors
        const qVec = this.getSentenceVector(query);
        const rVec = this.getSentenceVector(response.text.substring(0, 500));
        coherenceScore = this.cosineSimilarity(qVec, rVec);
      }

      // Apply coherence boosts
      coherenceScore = this._applyCoherenceBoosts(coherenceScore, query, response.text);

      const couplingState = C.getCouplingState ? C.getCouplingState(coherenceScore) : 'normal';

      // ── Quality Flagging ────────────────────────────────────────────────
      if (coherenceScore < (C.OPTIMAL_FLOOR || 0.4)) {
        this.stats.qualityFlags++;
        response.qualityFlag = 'low_coherence';
      }

      // ── Confidence Calibration ──────────────────────────────────────────
      const origConf = response.confidence || 0.7;
      let calibrated = this._calibrateConfidence(origConf, coherenceScore);
      response.confidence = calibrated;

      // ── Evidence Enrichment ─────────────────────────────────────────────
      if (this.chunkEmbeddings && this.chunkEmbeddings.length > 0 && context.chunks) {
        const qVec = this.getQueryEmbedding(query);
        const relevantChunks = this.findRelevantChunks(qVec, context.chunks, 3);
        
        if (relevantChunks.length > 0 && relevantChunks[0].score > 0.5) {
          response.evidenceChunks = relevantChunks.map(c => ({
            text: (c.text || '').substring(0, 200),
            score: Math.round(c.score * 100) / 100,
            index: c.index
          }));
          this.stats.evidenceAdditions++;
        }
      }

      // ── Key Phrases ─────────────────────────────────────────────────────
      if (this.keyPhrases.length > 0) {
        response.relevantKeyPhrases = this.keyPhrases
          .filter(p => response.text.toLowerCase().includes(p.phrase.toLowerCase()))
          .slice(0, 5)
          .map(p => p.phrase);
      }

      // ── Cluster Info ────────────────────────────────────────────────────
      if (this.semanticClusters && this.semanticClusters.length > 0) {
        response.clusterInfo = {
          totalClusters: this.semanticClusters.length,
          topTopics: this.semanticClusters
            .slice(0, 3)
            .map(c => c.topicLabel)
        };
      }

      // ── Metadata ────────────────────────────────────────────────────────
      response.enhanced223 = true;
      response.coherenceScore = Math.round(coherenceScore * 100) / 100;
      response.couplingState = couplingState;

      const elapsed = performance.now() - start;
      this.stats.avgLatency = (this.stats.avgLatency * (this.stats.enhanced223Passes - 1) + elapsed) / this.stats.enhanced223Passes;

      return response;

    } catch (err) {
      console.warn('[223 EMERGENCE] Enhancement error:', err.message);
      response.enhanced223 = false;
      return response;
    }
  },

  _buildStructuralContext(structure) {
    return {
      paragraphs: structure.totalParagraphs,
      sentences: structure.totalSentences,
      words: structure.totalWords,
      readingLevel: structure.readingLevel?.gradeLevel,
      readingTime: structure.readingLevel?.readingTime,
      documentType: structure.documentType?.type || 'unknown',
      documentTypeConfidence: structure.documentType?.confidence,
      hierarchyDepth: structure.hierarchy?.length > 0 
        ? Math.max(...structure.hierarchy.map(h => h.level || 0)) + 1 
        : 0,
      hasChapters: structure.hierarchy?.some(h => h.type === 'chapter') || false,
      hasSections: structure.hierarchy?.some(h => h.type === 'section' || h.type === 'numbered-section') || false,
      figureCount: structure.specialElements?.figures?.length || 0,
      tableCount: structure.specialElements?.tables?.length || 0,
      citationCount: structure.specialElements?.citations?.length || 0
    };
  },

  _applyCoherenceBoosts(coherenceScore, query, responseText) {
    // Entity-query coherence boost
    const entityMatch = query.match(/(?:who\s+is|what\s+is|about|describe)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i);
    if (entityMatch) {
      const entityName = entityMatch[1].toLowerCase();
      if (responseText.toLowerCase().includes(entityName) && coherenceScore < 0.60) {
        coherenceScore = Math.min(0.70, coherenceScore + 0.25);
        this.stats.coherenceBoosts++;
      }
    }

    // List-query boost
    const isListQ = /\b(list|who\s+are|what\s+are|name|names|people|mentioned)\b/i.test(query);
    if (isListQ && coherenceScore < 0.55) {
      const names = responseText.match(/[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?/g);
      if (names && names.length >= 2) {
        coherenceScore = Math.min(0.65, coherenceScore + 0.20);
      }
    }

    return coherenceScore;
  },

  _calibrateConfidence(origConf, coherenceScore) {
    let calibrated = origConf;

    // Low coherence penalty
    if (origConf > 0.8 && coherenceScore < 0.5) {
      calibrated = origConf * 0.7;
      this.stats.confidenceAdjustments++;
    }

    // Over-coupling penalty
    if (coherenceScore > 0.85 && origConf > 0.8) {
      calibrated = origConf * 0.85;
      this.stats.confidenceAdjustments++;
    }

    return calibrated;
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // WRITING QUALITY METRICS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Analyze writing quality using embeddings
   */
  analyzeWritingQuality(text) {
    if (!text || text.length < 50) {
      return { coherence: 0, focus: 0, development: 0, overall: 0 };
    }

    const sentences = this._splitIntoSentences(text);
    const metrics = {
      coherence: 0,
      focus: 0,
      development: 0,
      overall: 0
    };

    try {
      // Coherence: sentence-to-sentence flow
      if (sentences.length >= 2 && isModelTrained()) {
        let flowSum = 0;
        for (let i = 1; i < Math.min(sentences.length, 10); i++) {
          const prev = this.getQueryEmbedding(sentences[i - 1]);
          const curr = this.getQueryEmbedding(sentences[i]);
          flowSum += this.cosineSimilarity223(prev, curr);
        }
        metrics.coherence = flowSum / (Math.min(sentences.length, 10) - 1);
      }

      // Focus: consistency with document centroid
      if (this.documentCentroid) {
        const textVec = this.getQueryEmbedding(text.substring(0, 500));
        metrics.focus = this.cosineSimilarity223(textVec, this.documentCentroid);
      }

      // Development: sentence variety and length
      const lengths = sentences.map(s => s.split(/\s+/).length);
      const avgLen = lengths.reduce((a, b) => a + b, 0) / lengths.length;
      const variance = lengths.reduce((sum, len) => sum + (len - avgLen) ** 2, 0) / lengths.length;
      metrics.development = Math.min(1.0, (Math.sqrt(variance) / 10) + (sentences.length / 20));

      // Overall
      metrics.overall = (metrics.coherence * 0.4 + metrics.focus * 0.3 + metrics.development * 0.3);

    } catch (err) {
      console.warn('[223 EMERGENCE] Writing quality analysis error:', err.message);
    }

    // Round all metrics
    for (const key of Object.keys(metrics)) {
      metrics[key] = Math.round(metrics[key] * 100) / 100;
    }

    return metrics;
  },

  /**
   * Compare student text to passage for evidence alignment
   */
  measurePassageAlignment(studentText, topK = 3) {
    if (!studentText || !this.chunkEmbeddings) {
      return { maxSimilarity: 0, avgSimilarity: 0, alignedChunks: [] };
    }

    try {
      const studentVec = this.getQueryEmbedding(studentText);
      let maxSim = 0;
      let totalSim = 0;
      const alignedChunks = [];

      for (let i = 0; i < this.chunkEmbeddings.length; i++) {
        if (!this.chunkEmbeddings[i]) continue;
        
        const sim = this.cosineSimilarity223(studentVec, this.chunkEmbeddings[i]);
        maxSim = Math.max(maxSim, sim);
        totalSim += sim;

        if (sim > 0.5) {
          alignedChunks.push({
            index: i,
            similarity: Math.round(sim * 100) / 100,
            preview: (this.chunkTexts?.[i] || '').substring(0, 100)
          });
        }
      }

      const avgSim = totalSim / Math.max(this.chunkEmbeddings.filter(e => e).length, 1);

      return {
        maxSimilarity: Math.round(maxSim * 100) / 100,
        avgSimilarity: Math.round(avgSim * 100) / 100,
        alignedChunks: alignedChunks.sort((a, b) => b.similarity - a.similarity).slice(0, topK)
      };
    } catch (err) {
      console.warn('[223 EMERGENCE] Passage alignment error:', err.message);
      return { maxSimilarity: 0, avgSimilarity: 0, alignedChunks: [] };
    }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // STATUS & STATISTICS
  // ═══════════════════════════════════════════════════════════════════════════

  getStatusInfo() {
    return {
      status: this.status,
      progress: this.loadProgress,
      model: 'Pure 223 Intelligence (Enhanced)',
      stats: this.stats,
      enabled: this.enabled,
      chunksLoaded: this.chunkEmbeddings?.length || 0,
      clustersCreated: this.semanticClusters?.length || 0,
      keyPhrasesExtracted: this.keyPhrases?.length || 0,
      sentenceEmbeddings: this.sentenceEmbeddings?.length || 0,
      cacheSize: this.embeddingCache.size
    };
  },

  getClusterSummary() {
    if (!this.semanticClusters) return [];
    
    return this.semanticClusters.map((cluster, i) => ({
      id: i,
      label: cluster.topicLabel,
      size: cluster.members.length,
      coherence: Math.round(cluster.coherence * 100) / 100
    }));
  },

  getKeyPhrases(topN = 10) {
    return this.keyPhrases.slice(0, topN).map(p => ({
      phrase: p.phrase,
      count: p.count,
      score: Math.round(p.score * 100) / 100
    }));
  }
};

export default EmergenceLayer223;